import type { Message } from '../types';
import { signInThenResend } from '../services/reauth';

/**
 * Pure planning and orchestration for the "Sign in again" button, kept out of
 * App.tsx so it can be tested without a DOM.
 *
 * The click handler's snapshot of the chat is stale by the time the popup
 * closes, so the plan is made twice: once at the click (is the button valid?)
 * and once after sign-in against the CURRENT chat (is it still the same failed
 * turn?). Only then is anything re-sent.
 */

export interface ChatSnapshot {
  messages: Message[];
  isLoading: boolean;
  chatId: string | null;
}

/** Identifies the failed turn the button was offered for. */
export interface ResendTarget {
  failedId: string;
  chatId: string | null;
}

export type ResendPlan =
  | { ok: true; content: string; base: Message[]; target: ResendTarget }
  | { ok: false; reason: 'busy' | 'not-offered' | 'stale' };

const isUserMessage = (m: Message): boolean => m.isUser ?? m.sender === 'user';

/**
 * Whether the failed turn can be re-sent, and from which transcript. The failed
 * turn must be the NEWEST message (an AI message flagged requiresSignIn)
 * directly after the user message it answered; nothing may be in flight. With a
 * target, it must also still be that same message in that same chat.
 */
export function planResend(snapshot: ChatSnapshot, target?: ResendTarget): ResendPlan {
  const { messages, isLoading, chatId } = snapshot;
  if (isLoading) return { ok: false, reason: 'busy' };
  const failed = messages[messages.length - 1];
  const original = messages[messages.length - 2];
  if (!failed || isUserMessage(failed) || !failed.requiresSignIn || !original || !isUserMessage(original)) {
    return { ok: false, reason: target ? 'stale' : 'not-offered' };
  }
  if (target && (target.failedId !== failed.id || target.chatId !== chatId)) {
    return { ok: false, reason: 'stale' };
  }
  return {
    ok: true,
    content: original.content,
    base: messages.slice(0, -2),
    target: { failedId: failed.id, chatId },
  };
}

export type SignInAgainOutcome = 'not-offered' | 'cancelled' | 'stale' | 'resent';

let running: Promise<SignInAgainOutcome> | undefined;

/**
 * The button's click. Synchronously: plan, mark the sign-in pending (the caller
 * disables sending and the button), and open the sign-in. Do not await anything
 * before this runs: the popup must open inside the user's gesture. After
 * sign-in the plan is re-made against the current chat; a changed chat or a
 * newer message means nothing is re-sent.
 */
export function runSignInAgain(deps: {
  snapshot: ChatSnapshot;
  current: () => ChatSnapshot;
  setPending: (pending: boolean) => void;
  resend: (content: string, base: Message[]) => Promise<void> | void;
}): Promise<SignInAgainOutcome> {
  if (running) return running;
  const plan = planResend(deps.snapshot);
  if (!plan.ok) return Promise.resolve('not-offered');

  deps.setPending(true);
  let outcome: SignInAgainOutcome = 'cancelled';
  const run = signInThenResend(async () => {
    const again = planResend(deps.current(), plan.target);
    if (!again.ok) {
      outcome = 'stale';
      return;
    }
    outcome = 'resent';
    await deps.resend(again.content, again.base);
  })
    .then(() => outcome)
    .finally(() => {
      deps.setPending(false);
      running = undefined;
    });
  running = run;
  return run;
}
